import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import TuneIcon from "@mui/icons-material/Tune";

import { AdvancedSearchConfigModalProps } from "./types";
import { COLORS } from "./constants";
import {
  BoostConfigSection,
  FuzzyConfigSection,
  MatchPhraseConfigSection,
  MatchPhrasePrefixConfigSection,
  BooleanQueryConfigSection,
  ScriptScoreConfigSection,
} from "./components";
import {
  AdvancedSearchConfigDto,
  BoostConfigDto,
  FuzzyConfigDto,
  MatchPhraseConfigDto,
  MatchPhrasePrefixConfigDto,
  ScriptScoreConfigDto,
} from "@swagger/typescript-fetch-goflyer";
import {
  validateBoostValue,
  validateFuzziness,
  validatePrefixLength,
  validateMaxExpansions,
  validateSlop,
  validateMinimumShouldMatch,
  validateOneOrGreater,
  validateZeroOrGreater,
} from "./utils/validation";
import { messages } from "../../../locales/messages";

export const AdvancedSearchConfigModal: React.FC<
  AdvancedSearchConfigModalProps
> = ({
  open,
  onClose,
  onSave,
  zIndex = 1300,
  advancedSearchState,
  updateBoostConfig,
  updateFuzzyConfig,
  updateMatchPhraseConfig,
  updateMatchPhrasePrefixConfig,
  updateMinimumShouldMatch,
  updateScriptScoreConfig,
  updateBoostMode,
  toggleAdvancedSearch,
  updateKeepSearchResults,
  updateSearchLimit,
  updateSearchFlyerItems,
  resetToDefaults,
}) => {
  const { t } = useTranslation();
  const {
    isEnabled,
    config,
    keepSearchResults,
    searchLimit,
    searchFlyerItems,
  } = advancedSearchState;

  const [localConfig, setLocalConfig] =
    useState<AdvancedSearchConfigDto>(config);
  const [localIsEnabled, setLocalIsEnabled] = useState<boolean>(isEnabled);
  const [localKeepSearchResults, setLocalKeepSearchResults] =
    useState<boolean>(keepSearchResults);
  const [localSearchLimit, setLocalSearchLimit] = useState<number>(searchLimit);
  const [localSearchFlyerItems, setLocalSearchFlyerItems] =
    useState<boolean>(searchFlyerItems);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [hasValidationErrors, setHasValidationErrors] =
    useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setLocalConfig(config);
      setLocalIsEnabled(isEnabled);
      setLocalKeepSearchResults(keepSearchResults);
      setLocalSearchLimit(searchLimit);
      setLocalSearchFlyerItems(searchFlyerItems);
      setHasChanges(false);
    }
  }, [
    open,
    config,
    isEnabled,
    keepSearchResults,
    searchLimit,
    searchFlyerItems,
  ]);

  const checkForChanges = useCallback(() => {
    const configChanged =
      JSON.stringify(localConfig) !== JSON.stringify(config);
    const enabledChanged = localIsEnabled !== isEnabled;
    const keepResultsChanged = localKeepSearchResults !== keepSearchResults;
    const searchLimitChanged = localSearchLimit !== searchLimit;
    const searchFlyerItemsChanged = localSearchFlyerItems !== searchFlyerItems;
    setHasChanges(
      configChanged ||
        enabledChanged ||
        keepResultsChanged ||
        searchLimitChanged ||
        searchFlyerItemsChanged,
    );
  }, [
    localConfig,
    config,
    localIsEnabled,
    isEnabled,
    localKeepSearchResults,
    keepSearchResults,
    localSearchLimit,
    searchLimit,
    localSearchFlyerItems,
    searchFlyerItems,
  ]);

  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]);

  useEffect(() => {
    const cfg = localConfig;
    const boostConfig = cfg.boostConfig || {};
    const fuzzyConfig = cfg.fuzzyConfig || {};
    const matchPhraseConfig = cfg.matchPhraseConfig || {};
    const matchPhrasePrefixConfig = cfg.matchPhrasePrefixConfig || {};
    const scriptScoreConfig = cfg.scriptScoreConfig || {};

    const errors: Array<string | null> = [];

    errors.push(
      validateBoostValue(boostConfig.wildcard ?? ""),
      validateBoostValue(boostConfig.fuzzy ?? ""),
      validateBoostValue(boostConfig.termExact ?? ""),
      validateBoostValue(boostConfig.termFirstWord ?? ""),
      validateBoostValue(boostConfig.termGeneral ?? ""),
      validateBoostValue(boostConfig.match ?? ""),
      validateBoostValue(boostConfig.prefix ?? ""),
      validateBoostValue(boostConfig.multiMatch ?? ""),
    );

    errors.push(
      validateFuzziness(`${fuzzyConfig.fuzziness ?? ""}`),
      validatePrefixLength(fuzzyConfig.prefixLength ?? ""),
      validateMaxExpansions(fuzzyConfig.maxExpansions ?? ""),
    );

    errors.push(validateSlop(matchPhraseConfig.slop ?? ""));

    errors.push(
      validateSlop(matchPhrasePrefixConfig.slop ?? ""),
      validateMaxExpansions(matchPhrasePrefixConfig.maxExpansions ?? ""),
    );

    errors.push(validateMinimumShouldMatch(cfg.minimumShouldMatch ?? ""));

    errors.push(
      validateOneOrGreater(scriptScoreConfig.exactMatchMultiplier ?? ""),
      validateOneOrGreater(scriptScoreConfig.startsWithMultiplier ?? ""),
      validateZeroOrGreater(scriptScoreConfig.containsBase ?? ""),
      validateOneOrGreater(scriptScoreConfig.maxBoostCap ?? ""),
      validateOneOrGreater(scriptScoreConfig.boundaryBonus ?? ""),
    );

    setHasValidationErrors(errors.some((e) => !!e));
  }, [localConfig]);

  const localUpdateBoostConfig = useCallback(
    (updates: Partial<BoostConfigDto>) => {
      setLocalConfig((prev) => ({
        ...prev,
        boostConfig: {
          ...prev.boostConfig,
          ...updates,
        },
      }));
    },
    [],
  );

  const localUpdateFuzzyConfig = useCallback(
    (updates: Partial<FuzzyConfigDto>) => {
      setLocalConfig((prev) => ({
        ...prev,
        fuzzyConfig: {
          ...prev.fuzzyConfig,
          ...updates,
        },
      }));
    },
    [],
  );

  const localUpdateMatchPhraseConfig = useCallback(
    (updates: Partial<MatchPhraseConfigDto>) => {
      setLocalConfig((prev) => ({
        ...prev,
        matchPhraseConfig: {
          ...prev.matchPhraseConfig,
          ...updates,
        },
      }));
    },
    [],
  );

  const localUpdateMatchPhrasePrefixConfig = useCallback(
    (updates: Partial<MatchPhrasePrefixConfigDto>) => {
      setLocalConfig((prev) => ({
        ...prev,
        matchPhrasePrefixConfig: {
          ...prev.matchPhrasePrefixConfig,
          ...updates,
        },
      }));
    },
    [],
  );

  const localUpdateMinimumShouldMatch = useCallback((value: number) => {
    setLocalConfig((prev) => ({
      ...prev,
      minimumShouldMatch: value,
    }));
  }, []);

  const localUpdateScriptScoreConfig = useCallback(
    (updates: Partial<ScriptScoreConfigDto>) => {
      setLocalConfig((prev) => ({
        ...prev,
        scriptScoreConfig: {
          ...prev.scriptScoreConfig,
          ...updates,
        },
      }));
    },
    [],
  );

  const localUpdateBoostMode = useCallback(
    (mode?: AdvancedSearchConfigDto["boostMode"]) => {
      setLocalConfig((prev) => ({
        ...prev,
        boostMode: mode,
      }));
    },
    [],
  );

  const localToggleAdvancedSearch = useCallback((enabled: boolean) => {
    setLocalIsEnabled(enabled);
  }, []);

  const localToggleKeepSearchResults = useCallback((keep: boolean) => {
    setLocalKeepSearchResults(keep);
  }, []);

  const localUpdateSearchLimit = useCallback((limit: number) => {
    setLocalSearchLimit(limit);
  }, []);

  const localToggleSearchFlyerItems = useCallback((search: boolean) => {
    setLocalSearchFlyerItems(search);
  }, []);

  const handleSave = () => {
    if (hasChanges) {
      if (localIsEnabled !== isEnabled) {
        toggleAdvancedSearch(localIsEnabled);
      }

      if (localKeepSearchResults !== keepSearchResults) {
        updateKeepSearchResults(localKeepSearchResults);
      }

      if (localSearchLimit !== searchLimit) {
        updateSearchLimit(localSearchLimit);
      }

      if (localSearchFlyerItems !== searchFlyerItems) {
        updateSearchFlyerItems(localSearchFlyerItems);
      }

      if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
        if (
          JSON.stringify(localConfig.boostConfig) !==
          JSON.stringify(config.boostConfig)
        ) {
          updateBoostConfig(localConfig.boostConfig || {});
        }
        if (
          JSON.stringify(localConfig.fuzzyConfig) !==
          JSON.stringify(config.fuzzyConfig)
        ) {
          updateFuzzyConfig(localConfig.fuzzyConfig || {});
        }
        if (
          JSON.stringify(localConfig.matchPhraseConfig) !==
          JSON.stringify(config.matchPhraseConfig)
        ) {
          updateMatchPhraseConfig(localConfig.matchPhraseConfig || {});
        }
        if (
          JSON.stringify(localConfig.matchPhrasePrefixConfig) !==
          JSON.stringify(config.matchPhrasePrefixConfig)
        ) {
          updateMatchPhrasePrefixConfig(
            localConfig.matchPhrasePrefixConfig || {},
          );
        }
        if (localConfig.minimumShouldMatch !== config.minimumShouldMatch) {
          updateMinimumShouldMatch(localConfig.minimumShouldMatch || 1);
        }
        if (
          JSON.stringify(localConfig.scriptScoreConfig) !==
          JSON.stringify(config.scriptScoreConfig)
        ) {
          updateScriptScoreConfig &&
            updateScriptScoreConfig(localConfig.scriptScoreConfig || {});
        }
        if (localConfig.boostMode !== config.boostMode) {
          updateBoostMode && updateBoostMode(localConfig.boostMode);
        }
      }
    }

    // Always pass localConfig when enabled so that toggling on/off never
    // discards user-configured values — config is preserved regardless of
    // whether other changes were made in this session.
    onSave(localIsEnabled ? localConfig : undefined);
    onClose();
  };

  const handleCancel = () => {
    // Reset local state to original values
    setLocalConfig(config);
    setLocalIsEnabled(isEnabled);
    setLocalKeepSearchResults(keepSearchResults);
    setLocalSearchLimit(searchLimit);
    setLocalSearchFlyerItems(searchFlyerItems);
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex }}
      PaperProps={{
        sx: {
          minHeight: "70vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `2px solid ${COLORS.PRIMARY}`,
        }}
      >
        <TuneIcon sx={{ color: COLORS.PRIMARY }} />
        <Typography
          variant="h5"
          component="span"
          sx={{ color: COLORS.PRIMARY }}
        >
          {t(messages.Advanced_Search_Configuration())}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t(messages.Reset_to_Defaults())}>
          <IconButton
            onClick={resetToDefaults}
            sx={{
              color: COLORS.PRIMARY,
              "&:hover": {
                backgroundColor: COLORS.PRIMARY_LIGHT,
              },
            }}
          >
            <RestoreIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localIsEnabled}
              onChange={(e) => localToggleAdvancedSearch(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: COLORS.PRIMARY,
                  "&:hover": {
                    backgroundColor: COLORS.PRIMARY_ALPHA,
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: COLORS.PRIMARY,
                },
              }}
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6">
                {t(messages.Enable_Advanced_Search_Configuration())}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t(messages.Changes_Saved_on_Apply())}
              </Typography>
            </Box>
          }
          sx={{ mb: 3 }}
        />

        {/* Additional Search Settings */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t(messages.Search_Behavior_Settings())}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={localKeepSearchResults}
                onChange={(e) => localToggleKeepSearchResults(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: COLORS.PRIMARY,
                    "&:hover": {
                      backgroundColor: COLORS.PRIMARY_ALPHA,
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {t(messages.Keep_Search_Results_When_Creating_Flyer_Item())}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t(messages.Search_Results_Visibility_Tooltip())}
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: "flex-start" }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={localSearchFlyerItems}
                onChange={(e) => localToggleSearchFlyerItems(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: COLORS.PRIMARY,
                    "&:hover": {
                      backgroundColor: COLORS.PRIMARY_ALPHA,
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {t(messages.Search_In_Flyer_Item_Names())}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t(messages.Search_In_Flyer_Item_Names_Tooltip())}
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: "flex-start" }}
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t(messages.Search_Result_Limit())}
            </Typography>
            <TextField
              type="number"
              value={localSearchLimit}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value)) {
                  const clampedValue = Math.max(1, Math.min(1000, value));
                  localUpdateSearchLimit(clampedValue);
                }
              }}
              inputProps={{ min: 1, max: 1000, step: 10 }}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              helperText={t(messages.Search_Result_Limit_Helper())}
              sx={{
                width: "100%",
                maxWidth: 300,
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: COLORS.PRIMARY,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.PRIMARY,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  color: COLORS.PRIMARY,
                },
              }}
            />
          </Box>
        </Box>

        {localIsEnabled && (
          <Box>
            <BoostConfigSection
              config={{ boostConfig: localConfig.boostConfig }}
              updateBoostConfig={localUpdateBoostConfig}
            />

            <FuzzyConfigSection
              config={{ fuzzyConfig: localConfig.fuzzyConfig }}
              updateFuzzyConfig={localUpdateFuzzyConfig}
            />

            <MatchPhraseConfigSection
              config={{ matchPhraseConfig: localConfig.matchPhraseConfig }}
              updateMatchPhraseConfig={localUpdateMatchPhraseConfig}
            />

            <MatchPhrasePrefixConfigSection
              config={{
                matchPhrasePrefixConfig: localConfig.matchPhrasePrefixConfig,
              }}
              updateMatchPhrasePrefixConfig={localUpdateMatchPhrasePrefixConfig}
            />

            <BooleanQueryConfigSection
              config={{ minimumShouldMatch: localConfig.minimumShouldMatch }}
              updateMinimumShouldMatch={localUpdateMinimumShouldMatch}
            />

            <ScriptScoreConfigSection
              config={{
                scriptScoreConfig: localConfig.scriptScoreConfig,
                boostMode: localConfig.boostMode,
              }}
              updateScriptScoreConfig={localUpdateScriptScoreConfig}
              updateBoostMode={localUpdateBoostMode}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${COLORS.PRIMARY}` }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: COLORS.PRIMARY,
            color: COLORS.PRIMARY,
            "&:hover": {
              borderColor: COLORS.PRIMARY,
              backgroundColor: COLORS.PRIMARY_ALPHA,
            },
          }}
        >
          {t(messages.Cancel())}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {hasValidationErrors && (
          <Typography variant="body2" color={COLORS.ERROR} sx={{ mr: 2 }}>
            {t(messages.Fix_Validation_Errors_To_Apply_Settings())}
          </Typography>
        )}
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || hasValidationErrors}
          sx={{
            minWidth: 120,
            backgroundColor:
              hasChanges && !hasValidationErrors ? COLORS.PRIMARY : "#ccc",
            "&:hover": {
              backgroundColor:
                hasChanges && !hasValidationErrors
                  ? COLORS.PRIMARY_HOVER
                  : "#ccc",
            },
            "&:disabled": {
              color: "#666",
            },
          }}
        >
          {t(messages.Apply_Settings())}
          {hasChanges && !hasValidationErrors && (
            <Typography
              component="span"
              sx={{ ml: 1, fontSize: 12, opacity: 0.8 }}
            >
              •
            </Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
